import type { Node, NodeChange, NodeDimensionChange, NodePositionChange } from '../types';
import { applyItemChanges } from './applyItemChanges';

const applyDimensionChange = (node: Node, { dimensions, updateStyle, resizing }: NodeDimensionChange): Node => {
  const next: Node = { ...node };
  if (dimensions) {
    next.width = dimensions.width;
    next.height = dimensions.height;
    if (updateStyle) next.style = { ...node.style, ...dimensions };
  }
  if (typeof resizing === 'boolean') next.resizing = resizing;
  return next;
};

const applyPositionChange = (node: Node, { position, positionAbsolute, dragging }: NodePositionChange): Node => {
  const next: Node = { ...node };
  if (position) next.position = position;
  if (positionAbsolute) next.positionAbsolute = positionAbsolute;
  if (typeof dragging === 'boolean') next.dragging = dragging;
  return next;
};

const applyNodeChange = (node: Node, change: NodeChange): Node => {
  switch (change.type) {
    case 'dimensions':
      return applyDimensionChange(node, change);
    case 'position':
      return applyPositionChange(node, change);
    case 'select':
      return { ...node, selected: change.selected };
    case 'remove':
      return node;
  }
};

/**
 * Applies engine changes to the app's nodes (v11 `applyNodeChanges`): removed nodes are dropped, unchanged nodes keep
 * their identity, and new fields are appended after the node's own keys — the key order snapshot saves rely on.
 */
export const applyNodeChanges = <TNode extends Node>(changes: readonly NodeChange[], nodes: readonly TNode[]): TNode[] =>
  applyItemChanges(changes, nodes, (node, change) => applyNodeChange(node, change) as TNode);
